import type { OrderDashboardReport } from '@/api/hooks/useOrderDashboard'
import { SeverityTag } from '@/components/Display/SeverityDisplay'
import { formatCount } from '@/utils/stringFormatters'
import { InfoCircleOutlined } from '@ant-design/icons'
import { GraviGrid, Horizontal, Texto } from '@gravitate-js/excalibrr'
import type {
  ColDef,
  ICellRendererParams,
  RowClassParams,
  RowClickedEvent,
  ValueFormatterParams,
} from 'ag-grid-community'
import { Card, Tooltip } from 'antd'
import { useMemo } from 'react'
import { PercentText } from './PercentText'
import { isFilterableEvent } from './metricFilters'
import { metricRowClass, useMetricRowHighlight } from './metricRow'
import { type CheckpointRollup, rollupCheckpointMetrics } from './metrics'

// Tall enough for a severity tag to sit beside the event name.
const ROW_HEIGHT = 36
const HEADER_HEIGHT = 32

interface EventMetricsCardProps {
  report: OrderDashboardReport | undefined
  loading: boolean
  /** Label of the event the issue list is filtered to, if any. */
  selectedEvent: string | null
  /** Tenant name of the partner picked in the grid above, if any. The report
   * breaks its event counts down by partner, so these rows narrow with it. */
  selectedPartnerName: string | null
  /** Picks an event to filter the issues by; null clears the filter. */
  onSelectEvent: (label: string | null) => void
}

const EVENT_METRICS_HELP =
  'Each order integration event, counted across the window. Warnings and silenced events count towards the success rate; None is the number of orders that never reached the event at all, so it is left out of the rate. Picking a partner above narrows these counts to that partner.'

const countFormatter = (params: ValueFormatterParams<CheckpointRollup>) =>
  formatCount(params.value)

// Every column carries a field or colId: without one GraviGrid cannot match a
// column to its saved config, and they reshuffle as soon as one is added.
const columnDefs: ColDef<CheckpointRollup>[] = [
  {
    headerName: 'EVENT',
    field: 'label',
    flex: 1.8,
    minWidth: 150,
    cellRenderer: (params: ICellRendererParams<CheckpointRollup>) => (
      // Pushed apart rather than trailing the label, so the tags line up in
      // a column of their own instead of stepping in and out with the name.
      <Horizontal gap={6} verticalCenter justifyContent='space-between'>
        <Texto>{params.value}</Texto>
        <SeverityTag severity={params.data?.severity} />
      </Horizontal>
    ),
  },
  {
    headerName: '%',
    colId: 'percentage',
    field: 'percentage',
    type: 'numericColumn',
    // A one-character header, so the widest thing here is '100.0%'. What it
    // gives up goes to SUCCESS, whose header truncates once it takes a sort
    // arrow; the pair still adds up to the same share of the row.
    flex: 0.6,
    minWidth: 60,
    cellRenderer: (params: { value: number | null }) => (
      <PercentText value={params.value} />
    ),
  },
  {
    headerName: 'SUCCESS',
    field: 'success',
    type: 'numericColumn',
    flex: 1.1,
    minWidth: 95,
    valueFormatter: countFormatter,
  },
  {
    headerName: 'WARNING',
    field: 'warning',
    type: 'numericColumn',
    flex: 0.9,
    minWidth: 80,
    valueFormatter: countFormatter,
  },
  {
    headerName: 'FAILED',
    field: 'failed',
    type: 'numericColumn',
    flex: 0.8,
    minWidth: 75,
    valueFormatter: countFormatter,
  },
  {
    headerName: 'SILENCED',
    field: 'silenced',
    type: 'numericColumn',
    flex: 1,
    minWidth: 90,
    valueFormatter: countFormatter,
  },
  {
    headerName: 'NONE',
    field: 'none',
    type: 'numericColumn',
    flex: 0.8,
    minWidth: 70,
    valueFormatter: countFormatter,
  },
]

/** Per-event totals over the window, for one partner or for all of them. */
export function EventMetricsCard({
  report,
  loading,
  selectedEvent,
  selectedPartnerName,
  onSelectEvent,
}: EventMetricsCardProps) {
  const rows = useMemo(
    () => rollupCheckpointMetrics(report, selectedPartnerName),
    [report, selectedPartnerName],
  )

  const gridApiRef = useMetricRowHighlight(selectedEvent)

  // Clicking the row the list is already filtered to takes the filter back
  // off, matching how a category row inside a count card behaves.
  const selectRow = (event: RowClickedEvent<CheckpointRollup>) => {
    const label = event.data?.label
    if (!isFilterableEvent(label)) return
    onSelectEvent(label === selectedEvent ? null : (label ?? null))
  }

  return (
    <Card
      size='small'
      title={
        <Horizontal gap={6} verticalCenter>
          <Texto>Event Metrics</Texto>
          <Tooltip title={EVENT_METRICS_HELP}>
            <InfoCircleOutlined style={{ color: 'var(--gray-6)' }} />
          </Tooltip>
          {/* The partner row above is highlighted too, but the counts here
              change without the card saying why unless it names the partner. */}
          {selectedPartnerName && (
            <Texto appearance='hint'>{selectedPartnerName}</Texto>
          )}
        </Horizontal>
      }
      loading={loading}
    >
      <div className='dashboard-metrics-grid is-clickable'>
        <GraviGrid
          rowData={rows}
          columnDefs={columnDefs}
          storageKey='orders-dashboard-events-v3'
          sideBar={false}
          externalRef={gridApiRef}
          agPropOverrides={{
            getRowId: (params) => params.data.label,
            onRowClicked: selectRow,
            getRowClass: (params: RowClassParams<CheckpointRollup>) =>
              metricRowClass(params.data?.label === selectedEvent),
            suppressRowClickSelection: true,
            rowGroupPanelShow: 'never',
            headerHeight: HEADER_HEIGHT,
            rowHeight: ROW_HEIGHT,
            // The event list is a fixed set of checkpoints, so the grid can just
            // be as tall as it needs rather than scrolling inside the card.
            domLayout: 'autoHeight',
            defaultColDef: {
              sortable: true,
              resizable: true,
              suppressMovable: true,
            },
            overlayNoRowsTemplate:
              '<span style="color: var(--muted-foreground); font-size: 13px;">No events in this window</span>',
          }}
        />
      </div>
    </Card>
  )
}
